import { cn } from '@/utils/cn'
import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import { type ButtonHTMLAttributes, forwardRef } from 'react'

const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center whitespace-nowrap text-sm outline-none transition-all',
    'focus-visible:ring',
    'disabled:cursor-not-allowed'
  ),
  {
    variants: {
      variant: {
        primary: cn(
          'rounded-full',
          'bg-primary-500 font-medium text-button-foreground',
          'shadow-button-primary',
          'hocus:bg-primary-600',
          'disabled:bg-disabled disabled:hocus:bg-disabled disabled:opacity-100 disabled:shadow-button-primary-disabled'
        ),
        secondary: cn(
          'rounded-full',
          'bg-neutral-600 font-medium text-button-foreground',
          'shadow-button-secondary',
          'hocus:bg-neutral-700',
          'disabled:bg-disabled disabled:hocus:bg-disabled disabled:opacity-100'
        ),
        input: cn(
          'rounded-xl',
          'border bg-input text-body-foreground',
          'hover:border-body-foreground',
          'focus-visible:border-focus focus-visible:ring-2',
          'hocus:bg-input-hover hocus:text-active-foreground',
          'disabled:border-input-disabled disabled:hocus:border-input-disabled disabled:bg-input-disabled disabled:hocus:bg-input-disabled disabled:hocus:text-disabled-foreground disabled:text-disabled-foreground disabled:opacity-100'
        ),
        default: 'hocus:text-accent',
      },
      size: {
        icon: 'h-5 w-5',
        xs: 'h-6 px-3 text-xs',
        sm: 'h-8 px-5 text-sm',
        md: 'h-10 px-5',
        lg: 'h-12 px-6',
        default: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'sm',
    },
  }
)

type ButtonVariants = VariantProps<typeof buttonVariants>

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, ButtonVariants {
  asChild?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} type="button" {...props} />
  }
)
Button.displayName = 'Button'
